import { useViewerLocale } from "@/lib/translator";
import { ReturnRequestDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";

const toCents = (amount: number | null | undefined) =>
  Math.round((amount ?? 0) * 100);

// Mirror of the backend's isComplementTooSmallToCollect
// (stripe-complement-minimum.constant.ts, stripe-amount.ts): no payment is
// asked below 1.02, nor below Stripe's own minimum in the currency.
const COMPLEMENT_MINIMUM_CENTS = 102;
const STRIPE_MINIMUM_CHARGE: Record<string, number> = {
  AED: 2,
  CZK: 15,
  DKK: 2.5,
  HKD: 4,
  HUF: 175,
  JPY: 50,
  KRW: 50,
  MXN: 10,
  MYR: 2,
  NOK: 3,
  PLN: 2,
  RON: 2,
  SEK: 3,
  THB: 10,
};
const isComplementTooSmallToCollect = (cents: number, currency: string) => {
  const minimum = STRIPE_MINIMUM_CHARGE[currency.toUpperCase()];
  return (
    cents <= COMPLEMENT_MINIMUM_CENTS ||
    (minimum !== undefined && cents < minimum * 100)
  );
};

// BBS-363: the merchant saw neither the credit left nor the complement of a
// pure exchange-credit return before accepting it (BillingDetails only shows
// refund and discount lines). Before treatment, the figures of the creation:
// the amount given back drops with every rejected item. Once treated, what was
// actually given back — `rebuyRefund.amount`, summed at settlement
// (HandleRemainingCreditService), 0 when rejected items used the credit up;
// `rebuyRefund.done` is never set. The complement shown as paid is what
// Stripe captured (`rebuy.amount` + `reshipping.amount`, written by the
// payment webhook, or at creation when the webhook came first), not what was
// computed.
export default function ExchangeCreditSummary({
  returnRequest,
}: {
  returnRequest: Pick<
    ReturnRequestDto,
    "financial_informations" | "currency" | "status"
  >;
}) {
  const t = useTranslation();
  const locale = useViewerLocale();
  const { exchangeCredit, rebuy, reshipping, rebuyRefund } =
    returnRequest.financial_informations;

  const creditCents = toCents(exchangeCredit?.presentmentAmount);
  const rebuyCents = toCents(rebuy.presentmentAmount);
  const reshippingCents = toCents(reshipping.presentmentAmount);
  const complementCents = rebuyCents - creditCents + reshippingCents;
  const paidCents = toCents(rebuy.amount) + toCents(reshipping.amount);
  const hasIntent = !!returnRequest.financial_informations.intentId;
  const isComplementPaid = hasIntent && paidCents > 0;
  const isTreated = !!returnRequest.status.treatedMerchant.done;
  const estimatedRemainingCents = toCents(rebuyRefund.presentmentAmount);
  const settledCents = toCents(rebuyRefund.amount);

  if (!creditCents && !rebuyCents) {
    return null;
  }

  const format = (
    cents: number,
    currency = returnRequest.currency.customerCurrency,
  ) =>
    new Intl.NumberFormat(locale, { style: "currency", currency }).format(
      cents / 100,
    );

  const rows: { label: string; value: string; isTotal?: boolean }[] = [
    {
      label: t("returnDetail.exchangeCreditSummary.credit"),
      value: format(creditCents),
    },
    {
      label: t("returnDetail.exchangeCreditSummary.newArticles"),
      value: format(-rebuyCents),
    },
  ];
  if (reshippingCents) {
    rows.push({
      label: t("returnDetail.exchangeCreditSummary.reshipping"),
      value: format(-reshippingCents),
    });
  }
  // Charged less (or more) than due, beyond rounding: show what was due next
  // to what was captured, or the gap would go unnoticed.
  // Two cents, or one whole unit where the currency has no decimals (a JPY
  // complement is charged in whole yen, the amount due may not be).
  const fractionDigits =
    new Intl.NumberFormat("en", {
      style: "currency",
      currency: returnRequest.currency.customerCurrency,
    }).resolvedOptions().maximumFractionDigits ?? 2;
  const toleranceCents = fractionDigits === 0 ? 100 : 2;
  const isCapturedDifferentFromDue =
    isComplementPaid && Math.abs(paidCents - complementCents) > toleranceCents;
  if (complementCents > 0 && isCapturedDifferentFromDue) {
    rows.push({
      label: t("returnDetail.exchangeCreditSummary.complementDue"),
      value: format(complementCents),
    });
  }
  if (complementCents > 0) {
    rows.push({
      // An intent whose capture is not recorded yet (webhook still to come,
      // or a shop without Stripe Connect): neither "paid" nor "due".
      label: isComplementPaid
        ? t("returnDetail.exchangeCreditSummary.complement")
        : hasIntent
          ? t("returnDetail.exchangeCreditSummary.complementPending")
          : isComplementTooSmallToCollect(
                complementCents,
                returnRequest.currency.customerCurrency,
              )
            ? t("returnDetail.exchangeCreditSummary.complementWaived")
            : t("returnDetail.exchangeCreditSummary.complementDue"),
      value: format(isComplementPaid ? paidCents : complementCents),
      isTotal: true,
    });
  } else if (isComplementPaid) {
    // Captured while the return's own figures show nothing due (the two
    // pricing engines disagreeing): what was captured must still show.
    rows.push({
      label: t("returnDetail.exchangeCreditSummary.complement"),
      value: format(paidCents),
    });
  }
  if (
    complementCents <= 0 &&
    (estimatedRemainingCents > 0 || settledCents > 0)
  ) {
    rows.push({
      label:
        rebuyRefund.method === "discount"
          ? t("returnDetail.exchangeCreditSummary.remainingDiscount")
          : t("returnDetail.exchangeCreditSummary.remainingRefund"),
      value: isTreated
        ? format(settledCents, rebuyRefund.currency || undefined)
        : format(estimatedRemainingCents),
      isTotal: true,
    });
  }

  // Only about the credit left: nothing to say when a complement was due,
  // or when the basket used the credit up.
  const hasRemainingRow =
    complementCents <= 0 && (estimatedRemainingCents > 0 || settledCents > 0);
  const remainingHint = !hasRemainingRow
    ? null
    : !isTreated
      ? t("returnDetail.exchangeCreditSummary.estimateHint")
      : settledCents > 0
        ? t("returnDetail.exchangeCreditSummary.settledHint")
        : t("returnDetail.exchangeCreditSummary.settledNoneHint");

  return (
    <div className="mt-3 rounded-md border border-border p-3 text-sm">
      <p className="font-medium mb-2">
        {t("returnDetail.exchangeCreditSummary.title")}
      </p>
      <dl className="space-y-1">
        {rows.map((row) => (
          <div
            key={row.label}
            className={`flex justify-between gap-4 ${
              row.isTotal ? "border-t border-border pt-1 font-semibold" : ""
            }`}
          >
            <dt>{row.label}</dt>
            <dd className="tabular-nums">{row.value}</dd>
          </div>
        ))}
      </dl>
      {remainingHint && (
        <p className="mt-2 text-xs text-muted-foreground">{remainingHint}</p>
      )}
    </div>
  );
}
